import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";

import { apiClient } from "../api/client";
import { DefaultTermDateHttpResponse } from "../api/PartnerPortalGatewayApi";

export interface ITermOption {
  value: string;
  label: string;
  year: number;
  termNumber: number;
  startDate: dayjs.Dayjs;
  endDate: dayjs.Dayjs;
}

export const useTermDatesFromYear = (serviceId?: number | null, fromYear?: number) => {
  const currentYear = new Date().getFullYear();
  const defaultFromYear = fromYear || currentYear - 1;

  return useQuery({
    queryKey: ["termDatesFromYear", serviceId, defaultFromYear],
    queryFn: async (): Promise<DefaultTermDateHttpResponse[]> => {
      if (!serviceId) {
        return [];
      }
      return await apiClient.keyDates.getDefaultTermDatesFromYear(serviceId, defaultFromYear);
    },
    enabled: !!serviceId,
    staleTime: 1000 * 60 * 15, // 15 minutes
    gcTime: 1000 * 60 * 30, // 30 minutes
  });
};

export const useTermOptions = (serviceId?: number | null, fromYear?: number) => {
  const { data: termDates, isLoading, error } = useTermDatesFromYear(serviceId, fromYear);

  // Transform term data into dropdown options
  const termOptions: ITermOption[] = [];

  if (termDates) {
    termDates.forEach((term) => {
      termOptions.push({
        value: `${term.year}-T${term.termNumber}`,
        label: `${term.year} - Term ${term.termNumber}`,
        year: term.year,
        termNumber: term.termNumber,
        startDate: term.firstDayOfTerm,
        endDate: term.lastDayOfTerm,
      });
    });
  }

  // Sort by year and term number
  termOptions.sort((a, b) => {
    if (a.year !== b.year) return b.year - a.year; // Most recent year first
    return b.termNumber - a.termNumber; // Term 4, 3, 2, 1
  });

  return {
    termOptions,
    isLoading,
    error,
  };
};
